import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Dialog } from '@primer/react';
import { CJRButton } from '../../basicComponents/cjrButton/cjrButton';
import { Room } from '../../redux/models/room.model';
import { jsPDF } from 'jspdf';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  closeLoading,
  setManualLoading,
  showLoading,
  showModal,
} from '../../redux/slices/modal.slice';
import { ENTER_GAME_PATH } from '../../utils/constants';
import { useGetGameCodeQuery } from '../../redux/apis/auth.api';
import { useGetRoomsQuery } from '../../redux/apis/rooms';

export const QrCodeDialogMany: FunctionComponent<any> = ({
  setConfirm,
  confirm,
  rooms,
}: {
  readonly setConfirm: (confirm: boolean) => void;
  readonly confirm: boolean;
  readonly rooms: Room[];
}) => {
  const dispatch = useAppDispatch();
  const [startCreating, setStartCreating] = useState(false);
  const [pdfCreated, setPdfCreated] = useState(false);
  const loading = useAppSelector((state) => state.modal.loading);
  const getGameCodes = useGetGameCodeQuery(
    { roomIds: rooms.map((r) => r.roomId) },
    { skip: !confirm }
  );
  const getRooms = useGetRoomsQuery(undefined);

  useEffect(() => {
    if (getGameCodes.isSuccess) {
      if (Object.keys(getGameCodes.data).length === 0) {
        dispatch(
          showModal({ message: 'No game codes found, rooms do not exist.', type: 'danger' })
        );
        setConfirm(false);
        getRooms.refetch();
      }
    }
  }, [getGameCodes.isSuccess]);

  useEffect(() => {
    if (startCreating) {
      dispatch(setManualLoading(true));
      dispatch(showLoading());
    }
  }, [startCreating]);

  useEffect(() => {
    if (loading && startCreating) {
      createPDF();
      setStartCreating(false);
    }
  }, [loading]);

  useEffect(() => {
    if (pdfCreated) {
      setConfirm(false);
      dispatch(closeLoading());
      setPdfCreated(false);
    }
  }, [pdfCreated]);

  const createPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const gap = 7; // Gap between cards
    const cardWidth = pageWidth / 2 - gap - gap / 2; // Half page width minus gap
    const cardHeight = 50; // Fixed card height
    const qrCodeSize = 35; // QR code size
    let x = gap; // Initial x position
    let y = gap; // Initial y position

    rooms.forEach((room, index) => {
      // Draw card frame
      doc.rect(x, y, cardWidth, cardHeight);

      // Draw room name and ID
      doc.text(`${room.event}`, x + 5, y + 14);
      doc.text(`Event`, x + 5, y + 23);
      doc.text(`Room ${index + 1}`, x + 5, y + 38);
      doc.setFontSize(10); // Set smaller font size
      doc.text(
        window.location.origin +
          ENTER_GAME_PATH.replace(
            ':code',
            (getGameCodes.data! as { [roomId: string]: string })[room.roomId]
          ),
        x + 5,
        y + 45
      );
      doc.setFontSize(12); // Reset to default font size

      // Generate QR code URL and add image
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
        window.location.origin +
          ENTER_GAME_PATH.replace(
            ':code',
            (getGameCodes.data! as { [roomId: string]: string })[room.roomId]
          )
      )}`;
      doc.addImage(
        qrCodeUrl,
        'JPEG',
        x + cardWidth - qrCodeSize - 5,
        y + 5,
        qrCodeSize,
        qrCodeSize
      );

      // Update x and y for the next card
      x += cardWidth + gap;
      if (x + cardWidth > pageWidth) {
        x = gap; // Reset x to the start of the next row
        y += cardHeight + gap;
      }

      // If y exceeds page height, add a new page
      if (y + cardHeight > pageHeight) {
        doc.addPage();
        x = gap;
        y = gap;
      }
    });

    // Save the PDF
    doc.save('rooms-qr-codes.pdf');
    setPdfCreated(true);
  };

  return (
    <Dialog
      aria-labelledby="label"
      isOpen={confirm}
      onDismiss={() => setConfirm(false)}
      sx={{ height: 'initial !important' }}
    >
      <div
        style={{
          height: '80px',
          boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
          padding: '28px',
          display: 'flex',
          justifyContent: 'center',
          color: '#3B147A',
          textAlign: 'center',
          fontSize: '22px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: 'normal',
        }}
      >
        Download all QR codes
      </div>
      <Box p={5}>
        Click the Download button below to save an image with QR codes for all game rooms.
      </Box>
      <Box pb={5} pl={5} pr={5}>
        This is perfect for in-person sessions — players can easily scan a QR code to join a game
        room.
      </Box>
      <Box pb={8} pl={5} pr={5}>
        Tip: After downloading the image, you can either project it onto a large screen or print it
        out to place QR codes on separate tables. 👍
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        p={4}
        style={{ boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)' }}
      >
        <CJRButton
          onClick={() => setConfirm(false)}
          style={{ marginRight: '15px' }}
          variant="juicyTertiary"
        >
          Cancel
        </CJRButton>
        <CJRButton
          onClick={() => setStartCreating(true)}
          style={{ marginLeft: '15px' }}
          variant="juicyPrimary"
        >
          Download
        </CJRButton>
      </Box>
    </Dialog>
  );
};
